import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { ArchiveRestore } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';

// Helper pour extraire les valeurs RGB d'une couleur (supporte hex et rgb)
function parseRgbColor(color) {
    if (!color) return { r: 74, g: 144, b: 226 };

    // Format rgb(r, g, b)
    const rgbMatch = color.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
    if (rgbMatch) {
        return { r: rgbMatch[1], g: rgbMatch[2], b: rgbMatch[3] };
    }

    // Format hex #RRGGBB ou #RGB
    const hexMatch = color.match(/^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i);
    if (hexMatch) {
        return {
            r: parseInt(hexMatch[1], 16),
            g: parseInt(hexMatch[2], 16),
            b: parseInt(hexMatch[3], 16),
        };
    }

    // Format hex court #RGB
    const shortHexMatch = color.match(/^#?([a-f\d])([a-f\d])([a-f\d])$/i);
    if (shortHexMatch) {
        return {
            r: parseInt(shortHexMatch[1] + shortHexMatch[1], 16),
            g: parseInt(shortHexMatch[2] + shortHexMatch[2], 16),
            b: parseInt(shortHexMatch[3] + shortHexMatch[3], 16),
        };
    }

    return { r: 74, g: 144, b: 226 }; // Fallback
}

export default function ArchivedProcedureCard({ procedure, onRestore }) {
    const { t } = useTranslation();
    const rgb = parseRgbColor(procedure.color);
    const gradientStyle = {
        background: `linear-gradient(to right bottom, rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.082), rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.03))`,
    };

    const formattedDate = procedure.deleted_at
        ? format(new Date(procedure.deleted_at), 'dd/MM/yyyy', { locale: fr })
        : '??/??/????';

    return (
        <div
            className="group relative flex flex-col justify-between overflow-hidden rounded-[16px] border border-border bg-card transition-all hover:border-border hover:shadow-lg"
            style={gradientStyle}
        >
            <div className="flex flex-col items-center p-6 text-center">
                {/* Icon Circle */}
                <div
                    className="flex h-14 w-14 items-center justify-center rounded-full text-lg font-bold text-white shadow-sm mb-4"
                    style={{ backgroundColor: procedure.color }}
                >
                    {procedure.abbreviation}
                </div>

                {/* Procedure Name */}
                <h3 className="mb-2 text-base font-semibold text-foreground line-clamp-1">{procedure.name}</h3>

                {/* Description */}
                {procedure.description && (
                    <p className="mb-4 text-xs text-muted-foreground line-clamp-2">{procedure.description}</p>
                )}

                {/* Archived Date */}
                <p className="text-xs font-medium text-muted-foreground">{t('procedures:archiveeLe')} {formattedDate}</p>
            </div>

            {/* Actions */}
            <div className="p-4 pt-0 mt-auto">
                <Button
                    variant="solid"
                    color="secondary"
                    onClick={() => onRestore(procedure)}
                    className="w-full rounded-lg px-4 py-2.5 text-sm font-semibold"
                >
                    <ArchiveRestore className="h-4 w-4" />
                    {t('procedures:retablir')}
                </Button>
            </div>
        </div>
    );
}
